import { useState, useMemo } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import theme from 'theme';
import {
  Portlet,
  PortletToolbar,
  PortletContent,
  PortletFooter,
} from '../../../components/Portlets';
import {
  HARDWARE_TITLE_MAX,
  HARDWARE_DESCRIPTION_MAX,
  TEXT_FIELD_REGEX,
  TEXT_FIELD_MESSAGE,
} from 'constants/fieldLimits';

const HardwareRequestForm = ({ onSubmitButtonClick }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);
  const [descriptionTouched, setDescriptionTouched] = useState(false);

  const titleHelperText = useMemo(() => {
    if (titleTouched && !title) return 'Title is required';
    if (title.length >= HARDWARE_TITLE_MAX)
      return `Maximum length of ${HARDWARE_TITLE_MAX} characters reached.`;
    if (titleTouched && title && !TEXT_FIELD_REGEX.test(title)) return TEXT_FIELD_MESSAGE;
    return '';
  }, [titleTouched, title]);

  const descriptionHelperText = useMemo(() => {
    if (descriptionTouched && !description) return 'Description is required';
    if (description.length >= HARDWARE_DESCRIPTION_MAX)
      return `Maximum length of ${HARDWARE_DESCRIPTION_MAX} characters reached.`;
    if (descriptionTouched && description && !TEXT_FIELD_REGEX.test(description))
      return TEXT_FIELD_MESSAGE;
    return '';
  }, [descriptionTouched, description]);

  const titleError =
    titleTouched && (!title || title.length > HARDWARE_TITLE_MAX || !TEXT_FIELD_REGEX.test(title));
  const descriptionError =
    descriptionTouched &&
    (!description ||
      description.length > HARDWARE_DESCRIPTION_MAX ||
      !TEXT_FIELD_REGEX.test(description));
  const isValid =
    !!title &&
    !!description &&
    title.length <= HARDWARE_TITLE_MAX &&
    description.length <= HARDWARE_DESCRIPTION_MAX &&
    TEXT_FIELD_REGEX.test(title) &&
    TEXT_FIELD_REGEX.test(description);

  const handleReset = () => {
    setTitle('');
    setDescription('');
    setTitleTouched(false);
    setDescriptionTouched(false);
  };

  const handleSubmit = () => {
    const payload = {
      title,
      description,
    };
    onSubmitButtonClick(payload);
    handleReset();
  };

  return (
    <Portlet outlined={1} squared={false}>
      <PortletContent
        sx={{
          padding: '0 1.5rem',
          background: 'transparent',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          variant="body2"
          component="span"
          sx={{ fontWeight: 600, marginTop: theme.spacing(2), fontSize: '1rem' }}
        >
          Hardware Request
        </Typography>
        <Box sx={{ fontWeight: 600, marginTop: theme.spacing(2), fontSize: '1rem' }}>
          <TextField
            fullWidth
            id="title"
            label="Title"
            name="title"
            placeholder="Enter a title"
            multiline
            maxRows="4"
            value={title}
            onChange={e => setTitle(e.target.value)}
            margin="normal"
            variant="standard"
            error={titleError}
            onBlur={() => setTitleTouched(true)}
            helperText={titleHelperText}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { maxLength: HARDWARE_TITLE_MAX },
            }}
          />
        </Box>
        <Box sx={{ marginBottom: theme.spacing(2) }}>
          <TextField
            fullWidth
            id="description"
            label="Description"
            name="description"
            placeholder="Describe the request"
            multiline
            maxRows="4"
            value={description}
            onChange={e => setDescription(e.target.value)}
            onBlur={() => setDescriptionTouched(true)}
            error={descriptionError}
            margin="normal"
            variant="standard"
            helperText={descriptionHelperText}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { maxLength: HARDWARE_DESCRIPTION_MAX },
            }}
          />
        </Box>
      </PortletContent>
      <PortletFooter sx={{ borderTop: 'none', marginTop: '20%' }}>
        <PortletToolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={!title && !description}
            onClick={handleReset}
            variant="contained"
            sx={{
              bgcolor: 'primary.contrastText',
              color: 'common.black',
              '&:hover': { bgcolor: 'common.neutral' },
            }}
          >
            Reset
          </Button>
          <div>
            <Button
              disabled={!isValid}
              variant="contained"
              data-cy="hardwareSubmit"
              onClick={handleSubmit}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              Submit
            </Button>
          </div>
        </PortletToolbar>
      </PortletFooter>
    </Portlet>
  );
};

export default HardwareRequestForm;
